/**
 * Shared helpers for rules: context, rejection, lookups, beds, clocks, colours.
 *
 * Every rule function receives a Ctx. Validation helpers take PublicState so
 * they cannot reach `backs` (DESIGN §6.5).
 */
import { diag } from '../content/compendium.ts';
import { Rng } from '../prng.ts';
import type {
  Audience,
  BedKind,
  Box,
  Colour,
  DomainEvent,
  Patient,
  PatientId,
  Phase,
  PublicState,
  Role,
  Route,
  SeatState,
  SessionState,
  Step,
  TableState,
} from '../types.ts';
import { COLOUR_ORDER, CONFIRMED, ROLE_ORDER } from '../types.ts';

// ───────────────────────────── context ─────────────────────────────

export type Actor =
  | { kind: 'seat'; table: number; role: Role }
  | { kind: 'faculty'; id: string }
  | { kind: 'clock' }
  | { kind: 'system' };

export interface Ctx {
  s: SessionState;
  events: DomainEvent[];
  rng: Rng;
  at: number;
  actor: Actor;
}

/** Thrown by rules to reject a command; the message is shown to the player. */
export class Reject extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Reject';
  }
}

export function reject(msg: string): never {
  throw new Reject(msg);
}

export function ensure(cond: unknown, msg: string): asserts cond {
  if (!cond) throw new Reject(msg);
}

export interface EmitInput {
  type: string;
  table?: number | null;
  seat?: Role | null;
  patient?: PatientId | null;
  severity?: number;
  tag?: string;
  text: string;
  audience?: Audience[];
  data?: Record<string, unknown>;
}

export function emit(ctx: Ctx, e: EmitInput): void {
  ctx.events.push({
    type: e.type,
    table: e.table ?? null,
    seat: e.seat ?? null,
    patient: e.patient ?? null,
    turn: ctx.s.clock.turn,
    phase: ctx.s.clock.phase,
    severity: e.severity ?? 0,
    tag: e.tag,
    text: e.text,
    audience: e.audience ?? ['faculty'],
    data: e.data,
  });
}

// ───────────────────────────── lookups ─────────────────────────────

export function table(s: PublicState, no: number): TableState {
  const t = s.tables.find((x) => x.no === no);
  if (!t) reject(`No table ${no}.`);
  return t;
}

export function seat(t: TableState, role: Role): SeatState {
  const st = t.seats[role];
  if (!st) reject(`${roleName(role)} is not seated at this table.`);
  return st;
}

export function seatOpt(t: TableState, role: Role): SeatState | undefined {
  return t.seats[role];
}

export function seatedRoles(t: TableState): Role[] {
  return ROLE_ORDER.filter((r) => t.seats[r]);
}

export function patient(s: PublicState, id: PatientId): Patient {
  const p = s.patients[id];
  if (!p) reject('That patient is not here.');
  return p;
}

export function roleName(r: Role): string {
  return { EM: 'Emergency', SURG: 'the Surgeon', PHYS: 'the Physician', PSYCH: 'the Psychiatrist', ICU: 'the Intensivist' }[r];
}

export function roleTitle(r: Role): string {
  return { EM: 'Emergency', SURG: 'Surgeon', PHYS: 'Physician', PSYCH: 'Psychiatrist', ICU: 'Intensivist' }[r];
}

export const BED_OF: Record<Role, BedKind> = { EM: 'bay', SURG: 'SW', PHYS: 'MW', PSYCH: 'QW', ICU: 'ICU' };

export function seatOfBed(b: BedKind): Role {
  return ({ bay: 'EM', SW: 'SURG', MW: 'PHYS', QW: 'PSYCH', ICU: 'ICU' } as const)[b];
}

// ───────────────────────────── clocks & colours ─────────────────────────────

export function clockLength(c: Colour): number {
  return c === 'GREEN' ? 8 : c === 'AMBER' ? 6 : 4;
}

export function colourUp(c: Colour): Colour {
  const i = COLOUR_ORDER.indexOf(c);
  return COLOUR_ORDER[Math.min(2, i + 1)] as Colour;
}

export function colourRank(c: Colour): number {
  return COLOUR_ORDER.indexOf(c);
}

export function boxLabel(b: Box): string {
  return b === CONFIRMED ? '✓' : String(b);
}

export function boxUp(b: Box): Box {
  if (b === 50) return 70;
  if (b === 70) return 90;
  return CONFIRMED;
}

// ───────────────────────────── routes ─────────────────────────────

export function routeOf(p: Patient): Route | null {
  if (!p.route) return null;
  if (p.route.code === 'X') return null;
  const d = diag(p.route.diag);
  return d.routes.find((r) => r.code === p.route!.code) ?? null;
}

export function nextStep(p: Patient, route: Route | null = routeOf(p)): Step | null {
  if (!route || !p.route) return null;
  return route.steps[p.route.next] ?? null;
}

/** Steps outstanding on the current route, counting an owed Stabilise (DESIGN §6.5). */
export function outstandingSteps(p: Patient): number {
  const r = routeOf(p);
  const owed = p.needsStabilise ? 1 : 0;
  if (!r || !p.route) return owed;
  return r.steps.length - p.route.next + owed;
}

/** Remaining OBSERVE steps if only OBSERVE (+DISCHARGE) remain, else -1. */
export function onlyObservesRemain(p: Patient): number {
  const r = routeOf(p);
  if (!r || !p.route) return -1;
  const rest = r.steps.slice(p.route.next);
  if (rest.length === 0) return -1;
  let observes = 0;
  for (const st of rest) {
    if (st.kind === 'OBSERVE') observes++;
    else if (st.kind !== 'DISCHARGE') return -1;
  }
  return observes;
}

export function routeComplete(p: Patient): boolean {
  const st = nextStep(p);
  return !!st && st.kind === 'DISCHARGE' && !p.needsStabilise;
}

// ───────────────────────────── beds ─────────────────────────────

export function bedCap(s: PublicState, t: TableState, role: Role): number {
  const st = t.seats[role];
  if (!st) return 0;
  let cap = st.beds.length;
  if (role === 'PSYCH' && eventActive(s, 'E10')) cap = Math.min(cap, 2);
  return cap;
}

export function freeBedIndex(s: PublicState, t: TableState, role: Role): number {
  const st = t.seats[role];
  if (!st) return -1;
  const cap = bedCap(s, t, role);
  for (let i = 0; i < cap; i++) if (st.beds[i] === null || st.beds[i] === undefined) return i;
  return -1;
}

export function freeBeds(s: PublicState, t: TableState, role: Role): number[] {
  const st = t.seats[role];
  if (!st) return [];
  const cap = bedCap(s, t, role);
  const out: number[] = [];
  for (let i = 0; i < cap; i++) if (!st.beds[i]) out.push(i);
  return out;
}

/** Patients on a seat's list in display order: beds, then corridor. */
export function listOf(s: PublicState, t: TableState, role: Role): Patient[] {
  const st = t.seats[role];
  if (!st) return [];
  const ids = [...st.beds.filter((x): x is string => !!x), ...st.corridor];
  return ids.map((id) => s.patients[id]).filter((p): p is Patient => !!p && p.status === 'ACTIVE');
}

export function allActive(s: PublicState, t: TableState): Patient[] {
  return ROLE_ORDER.flatMap((r) => listOf(s, t, r));
}

export function bedKindOf(p: Patient): BedKind | 'corridor' | null {
  if (p.loc.kind === 'corridor') return 'corridor';
  if (p.loc.kind === 'bed' && p.seat) return BED_OF[p.seat];
  return null;
}

/** Remove a patient from wherever it sits on its table. */
export function unplace(s: SessionState, p: Patient): void {
  const t = s.tables.find((x) => x.no === p.table);
  if (t && p.seat) {
    const st = t.seats[p.seat];
    if (st) {
      st.beds = st.beds.map((b) => (b === p.id ? null : b));
      st.corridor = st.corridor.filter((x) => x !== p.id);
    }
  }
  p.seat = null;
  p.loc = { kind: 'none' };
}

/** Place a patient on a seat's list: a given bed index, the first free bed, or the corridor. */
export function place(s: SessionState, t: TableState, role: Role, p: Patient, where: number | 'corridor' | 'auto'): void {
  unplace(s, p);
  const st = seat(t, role);
  let idx = -1;
  if (where === 'auto') idx = freeBedIndex(s, t, role);
  else if (where !== 'corridor') {
    ensure(where >= 0 && where < bedCap(s, t, role) && !st.beds[where], 'That bed is not free.');
    idx = where;
  }
  p.table = t.no;
  p.seat = role;
  if (idx >= 0) {
    st.beds[idx] = p.id;
    p.loc = { kind: 'bed', index: idx };
  } else {
    if (role === 'ICU') reject('The ICU has no corridor.');
    st.corridor.push(p.id);
    p.loc = { kind: 'corridor' };
  }
}

// ───────────────────────────── events in play ─────────────────────────────

export function eventActive(s: PublicState, key: string): boolean {
  const turn = s.clock.turn;
  return s.room.activeEvents.some((e) => e.key === key && e.turn <= turn && turn <= e.until);
}

export function layerOn(s: PublicState, key: keyof SessionState['config']['layers']): boolean {
  return !!s.config.layers[key];
}

// ───────────────────────────── access ─────────────────────────────

/** Is this patient visible and actionable for a seat (own list, opened card, or E24 cover)? */
export function hasAccess(s: PublicState, t: TableState, role: Role, p: Patient): boolean {
  if (p.table !== t.no || p.status !== 'ACTIVE') return false;
  if (p.seat === role) return true;
  if (t.openCards.some((o) => o.patient === p.id && o.to === role)) return true;
  if (p.seat === 'PHYS' && coverActive(s, t) && t.effects.cover === role) return true;
  return false;
}

export function physicianAway(s: PublicState, t: TableState): boolean {
  return t.effects.physicianAwayUntil >= s.clock.turn;
}

export function coverActive(s: PublicState, t: TableState): boolean {
  return physicianAway(s, t) && !!t.effects.cover;
}

/** The roles a seat effectively holds for this patient (E24 cover adds PHYS on the Physician's list). */
export function effectiveRoles(s: PublicState, t: TableState, role: Role, p: Patient | null): Role[] {
  const roles: Role[] = [role];
  if (p && p.seat === 'PHYS' && coverActive(s, t) && t.effects.cover === role && role !== 'PHYS') roles.push('PHYS');
  return roles;
}

export function canPerform(step: { by: string[] }, roles: Role[]): boolean {
  if (step.by.includes('ANY')) return true;
  return roles.some((r) => step.by.includes(r));
}

// ───────────────────────────── misc ─────────────────────────────

export function nextId(s: SessionState, kind: 'handover' | 'interrupt' | 'prompt' | 'misc' | 'patient'): string {
  s.counters[kind] += 1;
  const n = s.counters[kind];
  const prefix = { handover: 'h', interrupt: 'i', prompt: 'q', misc: 'm', patient: 'p' }[kind];
  return `${prefix}${String(n).padStart(3, '0')}`;
}

export function isPhase(s: PublicState, ...phases: Phase[]): boolean {
  return phases.includes(s.clock.phase);
}

export function patientLabel(p: Patient): string {
  return p.name;
}

export function diagName(key: string): string {
  const d = diag(key);
  return d.frontName ?? d.name;
}

export function isRed(p: Patient): boolean {
  return p.colour === 'RED';
}

export function believedDiag(p: Patient) {
  return diag(p.believedKey);
}
